import Placeholder from '@components/LayoutTabs/SubsectionReusableComponents/Placeholder'
import TeamLogo from '@components/LayoutTabs/SubsectionReusableComponents/TeamLogo'
import { SvgIconName } from '@components/SvgIcon/types'
import useSportCompetitorsList from '@hooks/useSportCompetitorsList'
import translate from '@i18n'
import { SportCompetitorsListProps } from '@widgets/SportCompetitorsList'
import React, { FunctionComponent, useMemo } from 'react'
import styled, { css } from 'styled-components'

const StyledListWrapper = styled.div``
const StyledList = styled.ul`
  list-style: none;
  padding: 0;
`
const StyledListTeam = styled.li`
  ${({
    theme: {
      spacing: { spacing16, spacing8 },
      colors: { strokeWeak },
    },
  }) => css`
    padding: ${spacing16} 0;
    border-bottom: 1px solid ${strokeWeak};
    display: flex;
    gap: ${spacing8};
    align-items: center;
  `}
`
const StyledListTeamText = styled.div`
  ${({
    theme: {
      colors: { textDefault },
      typography: {
        subheads: {
          small1: { bundledCSS: small1SubheadCSS },
        },
      },
    },
  }) => css`
    color: ${textDefault};
    ${small1SubheadCSS};
  `}
`

const SportCompetitorsList: FunctionComponent<SportCompetitorsListProps> = ({
  seasonId,
  typeOfSport,
}) => {
  const {
    data: competitorsListData,
    error,
    isFetched,
  } = useSportCompetitorsList({
    typeOfSport,
    seasonId,
  })

  const hasNonEmptyData =
    competitorsListData && competitorsListData.items.length > 0

  const sportCompetitorsListContent = useMemo(() => {
    if (hasNonEmptyData) {
      return (
        <>
          {competitorsListData.items.map((competitor) => (
            <StyledListTeam key={competitor.sportradarId}>
              <TeamLogo
                name={competitor.image.alt}
                url={competitor.image.src}
                size={40}
              />
              <StyledListTeamText>{competitor.name}</StyledListTeamText>
            </StyledListTeam>
          ))}
        </>
      )
    } else {
      const placeholderMessage = error
        ? {
            iconName: 'user-robot-xmarks-solid' as SvgIconName,
            title: translate('network.error.title'),
            text: translate('network.error.text'),
          }
        : isFetched
          ? {
              iconName: 'calendar-clock' as SvgIconName,
              title: translate('sports.emptyData.title'),
              text: translate('sports.emptyData.text'),
            }
          : undefined

      return placeholderMessage ? (
        <Placeholder
          iconName={placeholderMessage.iconName}
          title={placeholderMessage.title}
          text={placeholderMessage.text}
        />
      ) : null
    }
  }, [competitorsListData, error, hasNonEmptyData, isFetched])

  return (
    <StyledListWrapper>
      <StyledList>{sportCompetitorsListContent}</StyledList>
    </StyledListWrapper>
  )
}

export default SportCompetitorsList
